import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { backupConfigAPI } from '../api/backupConfig';
import { toast } from 'react-toastify';
import './BackupConfigs.css';

const BackupConfigs = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10000
  });

  useEffect(() => {
    fetchConfigs();
  }, [pagination.page]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await backupConfigAPI.getAll(pagination.page, pagination.limit);
      setConfigs(response.data);
      setPagination(prev => ({
        ...prev,
        ...response.pagination
      }));
    } catch (error) {
      toast.error('Failed to fetch backup configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (configId, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      await backupConfigAPI.delete(configId);
      toast.success('Backup configuration deleted successfully');
      fetchConfigs();
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: { class: 'badge-success', icon: '✅', text: 'Success' },
      failed: { class: 'badge-danger', icon: '❌', text: 'Failed' },
      in_progress: { class: 'badge-warning', icon: '⏳', text: 'Running' },
      initiated: { class: 'badge-info', icon: '🔄', text: 'Starting' },
      partial_success: { class: 'badge-warning', icon: '⚠️', text: 'Partial' },
      pending: { class: 'badge-secondary', icon: '⏸️', text: 'Pending' }
    };
    return badges[status] || { class: 'badge-secondary', icon: '❓', text: status };
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  if (loading) {
    return <div className="backup-configs-container">Loading...</div>;
  }

  return (
    <div className="backup-configs-container">
      <div className="configs-header">
        <div>
          <h1>Backup Configurations</h1>
          <p className="subtitle">Manage all backup configurations across installations</p>
        </div>
        <Link to="/backup-configs/create" className="btn btn-success">
          + Create Configuration
        </Link>
      </div>

      {configs.length === 0 ? (
        <div className="empty-state">
          <h3>No Backup Configurations Found</h3>
          <p>Create your first backup configuration to get started</p>
          <Link to="/backup-configs/create" className="btn btn-success">
            Create Configuration
          </Link>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="configs-table">
              <thead>
                <tr>
                  <th>Configuration Name/Installation</th>
                  <th>Source Path</th>
                  <th>Destination</th>
                  <th>Status/Last Backup</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map((config) => {
                  const statusBadge = getStatusBadge(config.lastBackupStatus);
                  return (
                    <tr key={config._id}>
                      <td className="name-cell">
                        <div>
                          <strong>{config.configName}</strong>
                          {config.description && (
                            <>
                              <br />
                              <small className="description-text">
                                {config.description}
                              </small>
                            </>
                          )}
                        </div>
                        {config.installation?.uniqueId ? (
                          <Link to={`/installations/${config.installation?.uniqueId}`}>
                          {config.installation?.installationName || 'N/A'}
                        </Link>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </td>
                      <td className="path-cell">
                        <code>{config.sqlServerConfig?.database || 'N/A'}</code>
                        <span className="text-muted" style={{fontSize:'10px'}}>{config.sqlServerConfig?.host || 'N/A'}</span>
                      </td>
                      <td className="path-cell">
                        <span className="text-muted">
  {config.sftpServers?.length > 0 
    ? config.sftpServers.map(server => `${server.name} (${config.backupPath?config.backupPath:server.remotePath})`).join(', ')
    : 'N/A'}
</span>
                      </td>
                      <td>
                        <span className={`status-badge ${statusBadge.class}`}>
                          <span className="badge-icon">{statusBadge.icon}</span>
                          {statusBadge.text}
                        </span><br/>
                        <span className="text-muted" style={{fontSize:'10px'}}>{formatDate(config.lastBackupAt)}</span>
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <Link
                            to={`/backup-configs/${config._id}`}
                            className="btn btn-primary btn-sm"
                            title="View Details"
                          >
                            View
                          </Link>
                          <Link
                            to={`/backup-configs/edit/${config._id}`}
                            className="btn btn-secondary btn-sm"
                            title="Edit"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDelete(config._id, config.configName)}
                            className="btn btn-danger btn-sm"
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn btn-secondary"
              >
                Previous
              </button>
              <span className="page-info">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="btn btn-secondary"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BackupConfigs;
